import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import {
	useIntl,
} from 'react-intl';

import ChartTooltip from '~/components/patterns/charts/components/ChartTooltip';



type Props = {
	animate?: boolean;
	chartHeight?: number;
	data: Array<{
		data: Array<{
			name: string;
			y: number;
		}>;
	}>;
	disabled?: boolean;
	tooltipFormatter?: (context: Highcharts.TooltipFormatterContextObject) => React.ReactNode;
	viewportType: {
		isSmall: boolean;
	};
};

const DonutChart = (props: Props) => {
	const {
		animate = true,
		chartHeight,
		data,
		disabled = false,
		tooltipFormatter,
		viewportType,
	} = props;

	const chartRef = React.useRef<HighchartsReact.RefObject>(null);
	const intl = useIntl();

	const setDonutChartLowValuesToVisible = (chartData: Props['data']) => {
		if (!chartData.length || !chartData[0]?.data?.length) {
			return chartData;
		}

		const totalCount = chartData[0].data.reduce((a, dataSlice) => a + dataSlice.y, 0);
		const minSliceSize = 5;
		const minValue = Math.round((totalCount / 360) * minSliceSize);

		return [{
			...chartData[0],
			data: chartData[0].data.map((dataSlice) => ({
				...dataSlice,
				y: (dataSlice.y < minValue && dataSlice.y > 0) ? minValue : dataSlice.y,
			})),
		}];
	};

	const seriesData = React.useMemo(
		() => setDonutChartLowValuesToVisible(data),
		[
			data,
		],
	);

	const config = React.useMemo(
		() => ({
			chart: {
				backgroundColor: '#FFFFFF',
				height: chartHeight,
				spacing: [0, 0, 0, 0],
				type: 'pie',
			},
			plotOptions: {
				pie: {
					animation: animate,
					borderWidth: 1,
					dataLabels: {
						enabled: false,
					},
					innerSize: '59%',
					size: '90%',
					states: {
						hover: {
							halo: false,
						},
						inactive: {
							opacity: 1,
						},
					},
				},
			},
			series: seriesData.length ? seriesData : [],
			tooltip: {
				animation: false,
				backgroundColor: 'transparent',
				borderWidth: 0,
				enabled: !disabled && viewportType.isSmall === false,
				formatter: function (this: Highcharts.TooltipFormatterContextObject) {
					let content;

					if (tooltipFormatter) {
						content = tooltipFormatter(this);
					} else {
						content = intl.formatNumber(this.y || 0);
					}

					return ReactDOMServer.renderToString(
						<ChartTooltip color={this.color as string}>
							{content}
						</ChartTooltip>,
					);
				},
				padding: 0,
				shadow: false,
				shape: 'square',
				useHTML: true,
			},
		}),
		[
			animate,
			chartHeight,
			disabled,
			intl,
			seriesData,
			tooltipFormatter,
			viewportType,
		],
	);

	return (
		<HighchartsReact
			highcharts={Highcharts}
			options={config}
			ref={chartRef}
		/>
	);
};



export default DonutChart;
